// TIP-611 — Counterparty Briefs order form: ALL static data and copy.
// Companion to briefsFixtures.js, which owns the dashboard. Everything here
// comes from 02-customer-flows.md, Stage 2 ("Ordering a brief") and Stage 3
// ("The confirmation"), and from Counterparty Briefs.dc.html for the lists.
// Nothing is invented. Where this file departs from the design, the ticket
// calls for the departure and the departure is commented.

// The 45 countries, in the design's order: the UAE and the Gulf outward. One
// list serves three fields — requester country, subject country, and both
// mobile pickers — so a country cannot exist in one picker and not another.
export const COUNTRY_LIST = [
  { name: "United Arab Emirates", code: "AE", dial: "+971" },
  { name: "Saudi Arabia", code: "SA", dial: "+966" },
  { name: "Qatar", code: "QA", dial: "+974" },
  { name: "Kuwait", code: "KW", dial: "+965" },
  { name: "Bahrain", code: "BH", dial: "+973" },
  { name: "Oman", code: "OM", dial: "+968" },
  { name: "Jordan", code: "JO", dial: "+962" },
  { name: "Egypt", code: "EG", dial: "+20" },
  { name: "Lebanon", code: "LB", dial: "+961" },
  { name: "Iraq", code: "IQ", dial: "+964" },
  { name: "Turkey", code: "TR", dial: "+90" },
  { name: "India", code: "IN", dial: "+91" },
  { name: "Pakistan", code: "PK", dial: "+92" },
  { name: "Bangladesh", code: "BD", dial: "+880" },
  { name: "Sri Lanka", code: "LK", dial: "+94" },
  { name: "China", code: "CN", dial: "+86" },
  { name: "Hong Kong", code: "HK", dial: "+852" },
  { name: "Singapore", code: "SG", dial: "+65" },
  { name: "Malaysia", code: "MY", dial: "+60" },
  { name: "Indonesia", code: "ID", dial: "+62" },
  { name: "Vietnam", code: "VN", dial: "+84" },
  { name: "Japan", code: "JP", dial: "+81" },
  { name: "South Korea", code: "KR", dial: "+82" },
  { name: "Australia", code: "AU", dial: "+61" },
  { name: "United Kingdom", code: "GB", dial: "+44" },
  { name: "Ireland", code: "IE", dial: "+353" },
  { name: "Germany", code: "DE", dial: "+49" },
  { name: "France", code: "FR", dial: "+33" },
  { name: "Netherlands", code: "NL", dial: "+31" },
  { name: "Belgium", code: "BE", dial: "+32" },
  { name: "Spain", code: "ES", dial: "+34" },
  { name: "Italy", code: "IT", dial: "+39" },
  { name: "Switzerland", code: "CH", dial: "+41" },
  { name: "Poland", code: "PL", dial: "+48" },
  { name: "United States", code: "US", dial: "+1" },
  { name: "Canada", code: "CA", dial: "+1" },
  { name: "Brazil", code: "BR", dial: "+55" },
  { name: "South Africa", code: "ZA", dial: "+27" },
  { name: "Kenya", code: "KE", dial: "+254" },
  { name: "Nigeria", code: "NG", dial: "+234" },
  { name: "Ethiopia", code: "ET", dial: "+251" },
  { name: "Russia", code: "RU", dial: "+7" },
  { name: "Kazakhstan", code: "KZ", dial: "+77" },
  { name: "Azerbaijan", code: "AZ", dial: "+994" },
  { name: "Iran", code: "IR", dial: "+98" },
];

// Regional-indicator pair. No flag images ship for this list — 45 of them would
// be 45 requests for a decoration.
export const flagEmoji = (code) => {
  if (!code || code.length !== 2) return "🏳️";
  return String.fromCodePoint(
    ...code.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

export const ZONES = [
  "Not known",
  "Mainland",
  "Other freezone or authority",
  "DMCC",
  "JAFZA",
  "DAFZA",
  "DIFC",
  "Dubai South",
  "Dubai Silicon Oasis",
  "SHAMS (Sharjah)",
  "Hamriyah Free Zone",
  "SAIF Zone",
  "ADGM (Abu Dhabi)",
  "KIZAD",
  "Masdar City",
  "RAKEZ",
  "Ajman Free Zone",
  "Fujairah Free Zone",
  "UAQ Free Trade Zone",
  "Outside the UAE",
];

export const EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
  "Outside the UAE",
];

export const PURPOSES = [
  "New customer onboarding",
  "Credit limit review",
  "Overdue payment",
  "Supplier or vendor check",
  "Partnership or agency",
  "Tender or prequalification",
  "Insurance or finance application",
  "Other",
];

export const RELATIONSHIPS = [
  "They are our customer",
  "They are our supplier",
  "Prospective, no trade yet",
  "Agent or distributor",
  "Joint venture partner",
  "No relationship yet",
  "Other",
];

// The two option values that open a conditional free-text field. Named, because
// the conditional is a string comparison and a typo in it fails silently.
export const ZONE_OTHER = "Other freezone or authority";
export const OPTION_OTHER = "Other";

// TIP-611 — REQUESTER_PROFILE was here: a hardcoded "Faraz Ahmed" standing in
// for the claimed profile. It is gone. Section 1 now reads the real requester
// from /api/user/me/ and the selected company's details, mapped in
// requesterProfile.js. A placeholder that reaches a live POST files the brief
// under the wrong person and emails the analyst's questions to a stranger.

export const DEFAULT_DIAL = "+971";

// The verified contact records the directory lookup resolves against. One hit,
// as in the design. Replaced by the directory endpoint later; the lookup below
// is the only thing that reads it.
export const ORDER_COPY = {
  eyebrow: "Request form",
  title: "Order a Counterparty Brief",
  // TIP-611 fix: the design opens with "A fast pre-screening report". Screening
  // is internal risk vocabulary that appears nowhere else the customer can see,
  // and the page's own standfirst is plain language. It ships as "background".
  standfirst: (sourceNote) =>
    `A fast background report on any UAE or international business. Complete every field you can. ${sourceNote}`,
  close: "Close",

  header: {
    turnaroundLabel: "Turnaround",
    turnaround: "Within 1 business day",
    dateLabel: "Date of request",
    referenceLabel: "Your reference",
    referenceEmpty: "Add a contact email below",
  },

  // Which bucket this brief comes out of. Four wordings; the confirmation has
  // its own five, and the two sets must agree about the bought bucket.
  sourceNote: {
    week: (weekRemaining) => `This uses 1 of the ${weekRemaining} briefs you have left this week.`,
    month: (remaining) => `This uses 1 of the ${remaining} briefs you have left this month.`,
    boughtOne: "This uses your 1 bought brief.",
    boughtMany: (extra) => `This uses 1 of your ${extra} bought briefs.`,
  },

  sections: {
    one: "Your details (the requester)",
    two: "Company to be reported on (the subject)",
    three: "Your request",
    four: "Declaration",
  },

  requester: {
    fullName: "Full name",
    jobTitle: "Job title",
    jobTitlePlaceholder: "Finance Director",
    company: "Company / organisation",
    email: "Business email",
    mobile: "Mobile / WhatsApp",
    country: "Country",
    countryPlaceholder: "Select a country",
    countrySearch: "Search country",
  },

  subject: {
    legalName: "Registered legal name",
    legalNamePlaceholder: "Gulf Steel Trading FZE",
    // TIP-611 fix: the design labels this American ("license") two lines above a
    // British placeholder ("Trade licence"), and every other surface in the
    licence: "Commercial licence number",
    licencePlaceholder: "Trade licence or registration no.",
    zone: "Freezone or mainland",
    zonePlaceholder: "Not known",
    zoneSearch: "Search freezone or authority",
    zoneOther: "Which freezone or authority",
    zoneOtherPlaceholder: "Name the freezone, authority or registry",
    emirate: "Emirate or state",
    emiratePlaceholder: "Select where they are based",
    emirateSearch: "Search emirate or state",
    country: "Country",
    countryPlaceholder: "Select a country",
    countrySearch: "Search country",
    website: "Website",
    websitePlaceholder: "gulfsteel.ae",
    // TIP-611 — said once, under the pair. Two fields answering themselves and
    // then greying out needs explaining, and the note must not invite an edit
    // the field no longer accepts.
    outsideUaeNote:
      "Freezone and emirate are UAE answers, so they are set and locked for a company outside the UAE. Choose the UAE to edit them.",
  },

  reference: {
    heading: "Your reference at this company",
    sub: "Give us one email address we can reach out to. We fill in the rest.",
    optional: "Optional",
    email: "Reference email",
    emailPlaceholder: "ahmed.khan@gulfsteel.ae",
    matchHeading: "Matched in the TREVEX directory",
    matchLine: ({ name, role, company, dial, phone }) =>
      `${name}, ${role} at ${company}. ${dial} ${phone}.`,
    // No action to offer: a match fills the fields itself.
    matchFilled: "We have filled these in below.",
    looking: "Checking this address\u2026",
    noMatch:
      "No directory match yet. We read the name from the email. Correct anything that is wrong.",
    name: "Contact name",
    namePlaceholder: "Read from the email",
    role: "Job title",
    rolePlaceholder: "Finance Manager",
    mobile: "Contact mobile",
  },

  request: {
    purpose: "Purpose of request",
    purposePlaceholder: "Why you need this brief",
    purposeSearch: "Search purpose",
    purposeOther: "Tell us the purpose",
    relationship: "Trade relationship",
    relationshipPlaceholder: "How you deal with them",
    relationshipSearch: "Search relationship",
    relationshipOther: "Tell us the relationship",
    exposure: "Intended credit exposure / limit (AED)",
    exposurePlaceholder: "250,000",
    terms: "Proposed payment terms",
    termsPlaceholder: "30 / 60 / 90 days",
    notes: "Specific questions or notes for our analysts",
    notesPlaceholder: "Anything you already know, or a question you want answered.",
  },

  declaration:
    "This request is made for a legitimate business purpose and the information provided is accurate to the best of your knowledge. The report is supplied for internal risk and decisioning purposes only, does not constitute a recommendation to extend or withhold credit, and is subject to TREVEX terms of service. This service is provided by TREVEX Credit and Risk Solutions L.L.C-FZ, Dubai.",
  declarationTick: "I agree to the above.",

  footer: {
    // Which bucket the FOOTER names. Deliberately the plan wording in both
    // cases: the standfirst above already names the bought bucket when that is
    // what is being spent, and the two must not contradict each other.
    spendNote: {
      claimed: "Sending this uses 1 brief from this week's limit.",
      member: "Sending this uses 1 brief from this month's allowance.",
      bought: "Sending this uses 1 of your bought briefs.",
    },
    tail: "It goes back if we cannot produce the brief. Ready within 1 business day.",
    cancel: "Cancel",
    submit: "Send request",
    // TIP-611 — the press-to-response window. The design has no wording for
    // it, because it was drawn without an endpoint.
    sending: "Sending\u2026",
  },

  // TIP-611: the design never disables Send request, on any condition. This is
  // a legal attestation, so the submit is blocked until the box is ticked and
  // the six required fields carry a value.
  blockedHint: "Complete the required fields and agree to the declaration to send this request.",
};

export const CONFIRMATION_COPY = {
  eyebrow: "Request received",
  heading: (company) => `Your brief on ${company} is on its way.`,
  // The legal name is required, so this only shows if the field were ever made
  // optional. It is in the spec, so it is here.
  fallbackCompany: "this company",
  sentenceTail: "Nothing else is needed from you.",
  referenceLabel: "Reference",
  placedLabel: "Placed",
  readyLabel: "Ready by",
  ready: "Within 1 business day",
  stepsHeading: "What happens next",
  steps: [
    {
      label: "Order accepted.",
      detail: 'The request now shows as "Request received" in your briefs list.',
    },
    {
      label: "Brief being built.",
      detail:
        "Licence records, filings, web presence and payment signals are read and written into the brief.",
    },
    {
      label: "Ready to download.",
      detail:
        "The PDF appears on the row and we email you. If the company cannot be identified, the brief goes back to your allowance.",
    },
  ],
  primary: "Back to my briefs",
  orderAnother: "Order another brief",
  buyOne: "Buy a brief",
  buyMany: "Buy briefs",
};
